import { type UIEvent, useCallback, useRef, useState } from 'react';

import {
  getNextHideOnScrollState,
  type HideOnScrollState,
} from '../scroll/getNextHideOnScrollState';

/** Minimum scroll distance (in px) before the visibility changes, to ignore small jitters. */
const DEFAULT_SCROLL_THRESHOLD = 10;

export type HideOnScrollValue = {
  /** True while the user scrolls down (past the threshold), false as soon as they scroll up. */
  isHidden: boolean;
  /** Scroll handler to set on the scrollable element. */
  onScroll: (event: UIEvent<HTMLElement>) => void;
};

/**
 * Tracks the scroll direction of an element to hide something (a header…) while scrolling down
 * and show it back as soon as the user scrolls up or goes back to the top.
 *
 * @param threshold - Minimum scroll distance (in px) before the visibility changes.
 * @returns Whether the element should be hidden, and the scroll handler to set.
 */
export const useHideOnScroll = (threshold = DEFAULT_SCROLL_THRESHOLD): HideOnScrollValue => {
  const [isHidden, setIsHidden] = useState(false);
  const stateRef = useRef<HideOnScrollState>({ isHidden: false, lastScrollTop: 0 });

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      stateRef.current = getNextHideOnScrollState(event.currentTarget, stateRef.current, threshold);
      setIsHidden(stateRef.current.isHidden);
    },
    [threshold],
  );

  return { isHidden, onScroll };
};
